import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Check, Crown, Sparkles, Zap, Rocket, Flame, Loader2, ChevronLeft, ShieldCheck,
} from "lucide-react";

// Visual identity for each plan
const PLAN_VISUAL = {
  trial: { icon: Sparkles, gradient: "from-cyan-400 to-pink-500", label: "Free Trial" },
  basic: { icon: ShieldCheck, gradient: "from-emerald-400 to-cyan-400", label: "Basic" },
  featured: { icon: Crown, gradient: "from-amber-300 to-pink-500", label: "Featured" },
  vip: { icon: Crown, gradient: "from-amber-300 via-pink-500 to-cyan-400", label: "VIP" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function PlanCard({ planKey, plan, current, onChoose, disabled, isFeatured }) {
  const V = PLAN_VISUAL[planKey] || PLAN_VISUAL.basic;
  const Icon = V.icon;
  const isActive = current === planKey;
  return (
    <div
      className={`relative rounded-3xl border ${isFeatured ? "border-amber-400/40" : "border-white/10"} bg-white/[0.03] p-6 space-y-4 overflow-hidden`}
      data-testid={`plan-card-${planKey}`}
    >
      {isFeatured && (
        <div className="absolute top-0 right-0 px-3 py-1 text-[10px] font-black tracking-widest bg-gradient-to-r from-amber-300 to-pink-500 text-black rounded-bl-2xl">
          MOST POPULAR
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${V.gradient} grid place-items-center`}>
          <Icon className="w-5 h-5 text-black" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{V.label}</p>
          <h3 className="text-xl font-black tracking-tight">{plan.name}</h3>
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-4xl font-black">${plan.price_usd.toFixed(2)}</span>
        <span className="text-sm text-zinc-400">/month</span>
      </div>
      <p className="text-sm text-zinc-300 leading-snug">{plan.description}</p>
      <ul className="space-y-1.5">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
            <span className="text-zinc-200">{f}</span>
          </li>
        ))}
      </ul>
      <button
        disabled={disabled || isActive}
        onClick={() => onChoose(planKey)}
        className={`w-full h-12 rounded-full font-bold transition-colors ${
          isActive
            ? "bg-white/10 text-white cursor-default"
            : isFeatured
            ? "bg-gradient-to-r from-amber-300 to-pink-500 text-black hover:opacity-90"
            : "bg-pink-500 hover:bg-pink-400 text-white glow-pink"
        } disabled:opacity-50`}
        data-testid={`choose-plan-${planKey}`}
      >
        {isActive ? "Current plan" : `Subscribe to ${plan.name}`}
      </button>
    </div>
  );
}

function BoostCard({ boostKey, boost, onChoose, disabled }) {
  const palette = {
    product: { icon: Zap, color: "from-cyan-400 to-emerald-400" },
    homepage: { icon: Rocket, color: "from-pink-500 to-purple-500" },
    local_trending: { icon: Flame, color: "from-amber-300 to-rose-500" },
  };
  const P = palette[boostKey];
  const Icon = P.icon;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3" data-testid={`boost-card-${boostKey}`}>
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${P.color} grid place-items-center`}>
          <Icon className="w-4 h-4 text-black" />
        </div>
        <span className="text-lg font-black">${boost.price_usd.toFixed(2)}</span>
      </div>
      <div>
        <p className="font-bold">{boost.name}</p>
        <p className="text-xs text-zinc-400 mt-1 leading-snug">{boost.description}</p>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1.5">Runs {boost.duration_hours}h</p>
      </div>
      <button
        disabled={disabled}
        onClick={() => onChoose(boostKey, boost)}
        className="w-full h-10 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm disabled:opacity-40"
        data-testid={`buy-boost-${boostKey}`}
      >
        Buy boost
      </button>
    </div>
  );
}

export default function VendorSubscription() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [boostProductId, setBoostProductId] = useState("");
  const [myProducts, setMyProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/subscriptions/me");
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load subscription");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // If we landed back from Stripe with a session_id, poll for status
    const sid = new URLSearchParams(loc.search).get("session_id");
    if (sid) pollStatus(sid);
    // eslint-disable-next-line
  }, []);

  // Fetch vendor's products for boost picker
  useEffect(() => {
    if (!user?.is_vendor) return;
    api.get("/products", { params: { vendor_id: user.user_id, limit: 100 } })
      .then(({ data: d }) => { setMyProducts(d); if (d[0]) setBoostProductId(d[0].product_id); })
      .catch(() => { /* ignore */ });
  }, [user?.user_id, user?.is_vendor]);

  async function pollStatus(sessionId, attempts = 0) {
    if (attempts > 8) return toast.error("Payment status timed out — refresh in a minute.");
    try {
      // Try both endpoints — we don't know if it was a subscription or boost
      const route = loc.pathname.includes("/boost")
        ? `/boosts/checkout/status/${sessionId}`
        : `/subscriptions/checkout/status/${sessionId}`;
      const { data: s } = await api.get(route);
      if (s.payment_status === "paid") {
        toast.success("Payment successful 🎉");
        await refreshUser();
        load();
        navigate("/sell/upgrade", { replace: true });
        return;
      }
      if (s.status === "expired") {
        toast.error("Payment session expired.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error(e);
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2500);
    }
  }

  const choosePlan = async (planKey) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/subscriptions/checkout", {
        plan: planKey,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setBusy(false);
    }
  };

  const buyBoost = async (boostKey, boost) => {
    if (busy) return;
    if (boost.requires_product_id && !boostProductId) {
      return toast.error("Pick a product to boost first.");
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/boosts/checkout", {
        boost_type: boostKey,
        product_id: boost.requires_product_id ? boostProductId : null,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start boost checkout");
      setBusy(false);
    }
  };

  const cancelRenewal = async () => {
    if (!window.confirm("Cancel auto-renew? Your shop stays active until the end of the current period.")) return;
    await api.post("/subscriptions/cancel");
    toast.success("Auto-renew cancelled. Your shop remains active until the period ends.");
    load();
  };
  const resumeRenewal = async () => {
    await api.post("/subscriptions/resume");
    toast.success("Auto-renew resumed.");
    load();
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-zinc-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!data?.is_vendor) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 text-center space-y-4">
        <h1 className="text-3xl font-black tracking-tighter">Become a vendor first</h1>
        <p className="text-zinc-400">Open a Hidden Gem shop to access subscriptions and boosts.</p>
        <button
          onClick={() => navigate("/sell")}
          className="h-11 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
        >
          Become a vendor
        </button>
      </div>
    );
  }

  const ent = data.entitlements;
  const plans = ent.plans || {};
  const boosts = ent.boosts || {};
  const currentVisual = PLAN_VISUAL[ent.plan] || PLAN_VISUAL.basic;
  const Icon = currentVisual.icon;

  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-7 pb-24" data-testid="vendor-subscription-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="sub-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {/* VIP private hero — only this vendor sees this card */}
      {ent.is_vip && (
        <div
          className="relative rounded-3xl p-6 overflow-hidden border border-amber-400/40 bg-gradient-to-br from-amber-300/10 via-pink-500/10 to-cyan-400/10"
          data-testid="vip-hero"
        >
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-300/20 blur-3xl" />
          <div className="absolute -bottom-16 -left-12 w-44 h-44 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-300 via-pink-500 to-cyan-400 grid place-items-center shrink-0">
              <Crown className="w-6 h-6 text-black" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-widest font-black text-amber-200">VIP Member · Private</p>
              <h2 className="text-2xl font-black tracking-tight">You're a VIP on Hidden Gem</h2>
              <p className="text-sm text-zinc-300 mt-1">Every Featured-plan perk is unlocked. <span className="font-bold text-white">No subscription fees, no boost gating, no expiration.</span></p>
              <p className="text-[11px] text-zinc-400 mt-1">Only you can see this badge. Buyers and other vendors never know.</p>
            </div>
          </div>
        </div>
      )}

      {/* Current status hero — hidden for VIPs (the VIP hero replaces it) */}
      {!ent.is_vip && (
      <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-6 space-y-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${currentVisual.gradient} grid place-items-center`}>
            <Icon className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Current plan</p>
            <h1 className="text-2xl font-black tracking-tight" data-testid="current-plan-name">
              {ent.plan === "trial" ? "Free Trial" : ent.plan === "inactive" ? "Inactive" : (plans[ent.plan]?.name || ent.plan)}
            </h1>
          </div>
          {ent.founding_vendor && (
            <span className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-300 to-pink-500 text-black text-[11px] font-black uppercase tracking-widest" data-testid="founding-vendor-badge">
              <Crown className="w-3 h-3" /> Founding Vendor
            </span>
          )}
        </div>

        {ent.is_trial && (
          <p className="text-sm text-zinc-300">
            <span className="text-pink-300 font-bold" data-testid="trial-days-left">{ent.trial_days_left}</span> days left on your free trial. Trial ends <span className="font-bold">{fmtDate(ent.trial_ends_at)}</span>. After that, pick Basic or Featured to keep selling.
          </p>
        )}
        {!ent.is_trial && ent.plan !== "inactive" && (
          <p className="text-sm text-zinc-300">
            {ent.cancel_at_period_end
              ? <>Auto-renew is off. Your shop stays active until <span className="font-bold">{fmtDate(ent.current_period_end)}</span>.</>
              : <>Next renewal: <span className="font-bold">{fmtDate(ent.current_period_end)}</span></>}
          </p>
        )}
        {ent.plan === "inactive" && (
          <p className="text-sm text-amber-300 font-bold">Your shop is currently inactive. Pick a plan below to get back online.</p>
        )}

        {!ent.is_trial && ent.plan !== "inactive" && (
          <div className="flex gap-2">
            {ent.cancel_at_period_end ? (
              <button onClick={resumeRenewal} className="h-10 px-4 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-sm font-bold" data-testid="resume-renewal">Resume auto-renew</button>
            ) : (
              <button onClick={cancelRenewal} className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold" data-testid="cancel-renewal">Cancel auto-renew</button>
            )}
          </div>
        )}
      </div>
      )}

      {/* Plans — hidden for VIPs */}
      {!ent.is_vip && (
      <div>
        <h2 className="text-2xl font-black tracking-tighter mb-3">Pick a plan</h2>
        <p className="text-sm text-zinc-400 mb-4">Affordable and built for small kitchens. Buyers always pay you directly — Hidden Gem never takes a cut of your sales.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PlanCard planKey="basic" plan={plans.basic} current={ent.plan} onChoose={choosePlan} disabled={busy} />
          <PlanCard planKey="featured" plan={plans.featured} current={ent.plan} onChoose={choosePlan} disabled={busy} isFeatured />
        </div>
      </div>
      )}

      {/* Boosts */}
      <div>
        <h2 className="text-2xl font-black tracking-tighter mb-3">Optional boosts</h2>
        <p className="text-sm text-zinc-400 mb-4">Light-touch, no commitment. Pay once, get 24 hours of extra reach.</p>
        {myProducts.length > 0 && (
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Product to boost</label>
            <select
              value={boostProductId}
              onChange={(e) => setBoostProductId(e.target.value)}
              className="mt-1 w-full h-11 bg-white/5 border border-white/10 rounded-xl text-white px-3 text-sm"
              data-testid="boost-product-select"
            >
              {myProducts.map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name} · ${p.price?.toFixed(2)}</option>
              ))}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <BoostCard boostKey="product" boost={boosts.product} onChoose={buyBoost} disabled={busy} />
          <BoostCard boostKey="homepage" boost={boosts.homepage} onChoose={buyBoost} disabled={busy} />
          <BoostCard boostKey="local_trending" boost={boosts.local_trending} onChoose={buyBoost} disabled={busy} />
        </div>
      </div>

      {/* Reassurance */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-zinc-300 space-y-2">
        <p className="font-bold text-white">Built to support small vendors — not extract from them.</p>
        <ul className="space-y-1 text-zinc-400">
          <li>• Buyer-to-vendor payments go directly to you via Cash App, Zelle, Apple Pay, or in person.</li>
          <li>• Hidden Gem only charges the optional monthly plan + any boosts you choose — never a percentage of your sales.</li>
          <li>• Cancel anytime. Your shop stays live until your current period ends.</li>
          <li>• Apple Pay and Google Pay are supported at checkout.</li>
        </ul>
      </div>
    </div>
  );
}
